import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Board } from '@/entities/board';
import {
  UserSelect,
  useBoardOwnership,
  useChangeBoardOwner,
  useParticipants,
  useSetOriginTeam,
  useTeams,
} from '@/entities/collab';
import { FormError } from '@/shared/forms';
import { Button, Combobox, ConfirmDialog, ErrorState, LoadingState, useToast } from '@/shared/ui';

import styles from './BoardAccess.module.css';

const STEP_KEY = {
  origin_team: 'ownership.stepOriginTeam',
  first_team: 'ownership.stepFirstTeam',
  default_owner: 'ownership.stepDefaultOwner',
  workspace_owner: 'ownership.stepWorkspaceOwner',
} as const;

/**
 * Who owns the board: the current owner, the immutable creator, the team it was created for and
 * where ownership would go if the owner left. Handing the board over and revoking the previous
 * owner's access are two different operations, and the UI keeps them apart.
 */
export function OwnershipPanel({
  board,
  workspaceId,
}: {
  board: Board;
  workspaceId: string | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const ownership = useBoardOwnership(board.id);
  const participants = useParticipants(board.id);
  const teams = useTeams(workspaceId ?? '', workspaceId !== null);
  const change = useChangeBoardOwner(board.id);
  const setOrigin = useSetOriginTeam(board.id);
  const [pick, setPick] = useState<string | null>(null);
  const [revoke, setRevoke] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (ownership.isPending) return <LoadingState />;
  if (ownership.isError) return <ErrorState error={ownership.error} />;
  const info = ownership.data;
  // People who may own the board: workspace members that can see it (never a board-only guest).
  const candidates = (participants.data ?? [])
    .filter((p) => p.is_workspace_member && p.user_id !== info.owner_user_id)
    .map((p) => ({ user_id: p.user_id, display_name: p.display_name }));

  return (
    <section className={styles.body} aria-label={t('ownership.title')} data-testid="ownership">
      <h3 className={styles.panelTitle}>{t('ownership.title')}</h3>
      <dl className={styles.facts}>
        <dt>{t('ownership.owner')}</dt>
        <dd>{info.owner_name}</dd>
        <dt>{t('ownership.creator')}</dt>
        <dd>{info.created_by_name ?? '—'}</dd>
        {info.origin_team_id !== null && (
          <>
            <dt>{t('ownership.originTeam')}</dt>
            <dd>{info.origin_team_name}</dd>
          </>
        )}
        {info.succession && (
          <>
            <dt>{t('ownership.successor')}</dt>
            <dd>
              {info.succession.display_name} · {t(STEP_KEY[info.succession.step])}
            </dd>
          </>
        )}
      </dl>
      <p className={styles.sub}>{t('ownership.hint')}</p>
      {info.can_manage && workspaceId !== null && (
        <div className={styles.body}>
          <span className={styles.sub}>{t('ownership.originTeamLabel')}</span>
          <Combobox
            label={t('ownership.originTeamLabel')}
            value={info.origin_team_id ?? '__none__'}
            size="sm"
            searchPlaceholder={t('common.search')}
            emptyText={t('common.noResults')}
            disabled={setOrigin.isPending}
            options={[
              { value: '__none__', label: t('ownership.noOriginTeam') },
              ...(teams.data ?? []).map((team) => ({ value: team.id, label: team.name })),
            ]}
            onChange={(next) => {
              setOrigin.mutate(next === '__none__' ? null : next);
            }}
          />
        </div>
      )}
      {info.can_manage && (
        <div className={styles.body}>
          <span className={styles.sub}>{t('ownership.newOwner')}</span>
          <UserSelect
            label={t('ownership.newOwner')}
            users={candidates}
            value={pick}
            placeholder={t('ownership.pickOwner')}
            onChange={setPick}
          />
          {info.can_revoke && (
            <label className={styles.person}>
              <input
                type="checkbox"
                checked={revoke}
                onChange={(event) => {
                  setRevoke(event.target.checked);
                }}
              />
              <span>{t('ownership.revokeAccess')}</span>
            </label>
          )}
          <p className={revoke ? styles.warn : styles.sub}>
            {revoke ? t('ownership.revokeHint') : t('ownership.handoverHint')}
          </p>
          <FormError error={change.error ?? setOrigin.error} />
          <div className={styles.actions}>
            <Button
              variant={revoke ? 'danger' : 'primary'}
              disabled={change.isPending}
              onClick={() => {
                setConfirming(true);
              }}
            >
              {revoke ? t('ownership.revokeAction') : t('ownership.handoverAction')}
            </Button>
          </div>
        </div>
      )}
      {confirming && (
        <ConfirmDialog
          title={revoke ? t('ownership.revokeTitle') : t('ownership.handoverTitle')}
          message={
            revoke
              ? t('ownership.revokeConfirm', { owner: info.owner_name })
              : t('ownership.handoverConfirm', { board: board.name })
          }
          confirmLabel={revoke ? t('ownership.revokeAction') : t('ownership.handoverAction')}
          pending={change.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            change.mutate(
              { userId: pick, revokeAccess: revoke },
              {
                onSuccess: () => {
                  setConfirming(false);
                  setPick(null);
                  setRevoke(false);
                  toast.show({ message: t('ownership.done') });
                },
                onError: () => {
                  setConfirming(false);
                },
              },
            );
          }}
        />
      )}
    </section>
  );
}
